import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronsUpDownIcon, LogOutIcon, SettingsIcon } from "lucide-react";
import type React from "react";
import type { User } from "@/features/auth/services/firebase";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * AccountChip — the pinned bottom of the sidebar. Shows the signed-in identity
 * and a menu to reach Settings (which hosts team + billing management, since
 * those have no standalone routes) and to sign out.
 *
 * When `collapsed`, the name/email/chevron drop to just the Avatar; the
 * trigger's `aria-label` already carries the accessible name regardless, and
 * a Tooltip adds the visible identity back for sighted hover/focus.
 */
export function AccountChip({
  user,
  profile,
  onSignOut,
  onNavigate,
  collapsed = false,
}: {
  user: User;
  profile: UserProfile | null;
  onSignOut: () => void;
  onNavigate?: () => void;
  collapsed?: boolean;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const name = profile?.name || user.email || "Vardic";
  const email = user.email ?? "";

  const go = (path: string): void => {
    onNavigate?.();
    void navigate(path);
  };

  const trigger = (
    <DropdownMenuTrigger
      aria-label={t("shell.accountMenu")}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-[var(--r-md)] border border-transparent py-2 text-left outline-none transition-colors hover:bg-[var(--nav-bg-2)] data-[popup-open]:bg-[var(--nav-bg-2)]",
        collapsed ? "justify-center px-0" : "px-2.5",
      )}
      data-test="account-chip"
      render={<button type="button" />}
    >
      <Avatar name={name} size={32} tone="brand" />
      {!collapsed && (
        <>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-[13px] text-[var(--nav-ink)]">
              {name}
            </div>
            <div className="truncate text-[11px] text-[var(--nav-ink-faint)]">
              {email}
            </div>
          </div>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-[var(--nav-ink-2)]" />
        </>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipPopup side="right">{name}</TooltipPopup>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent align="end" className="w-[232px]" side="top">
        <DropdownMenuLabel>{email}</DropdownMenuLabel>
        <DropdownMenuItem
          data-test="account-settings"
          onClick={() => go("/settings")}
        >
          <SettingsIcon />
          {t("nav.settings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-test="account-logout"
          onClick={onSignOut}
          variant="danger"
        >
          <LogOutIcon />
          {t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
